import { StudyState } from "@app/types";
import { ReviewsService } from "@core/application/reviews.service";
import { Search } from "@core/domain/models/search.model";
import { Review } from "@core/domain/models/review.model";
import { Id } from "@core/domain/types/id.type";
import type { SaveMedicalAlternativesAttributeValueDto } from "@evidence/sdk";
import { computed, Ref, ref, InjectionKey, onMounted, watch, onUnmounted } from "vue";
import useStudiesFiltering from "./Execute/SearchAndFilters/use-studies-filtering";
import useStudiesSorting from "./Execute/Sort/use-studies-sorting";
import { OxfordLevelOfEvidenceType } from "@core/domain/types/oxford-level-of-evidence-type";
import { MetaData } from "@core/domain/types/meta-data.type";
import { AttributeStructure } from "@core/domain/models/data-extraction-plan-attribute";
import { BuiltInImportSourceId } from "@core/domain/types/builtInImportSourceId";
import { ImportSourceType } from "@core/domain/types/import-source-type.type";
import { injectStrict } from "@app/utils/injectStrict";
import { EvidenceServiceApiKey, ProjectsServiceKey, ReviewsServiceKey } from "@app/injectionKeys";
import { ReviewLockState } from "@core/domain/types/reviewLockState.type";

import { InclusionCriterion } from "@core/domain/models/InclusionCriterion.model";
import { PeerReviewStatus } from "@core/domain/types/peerReview.type";
import { EventDispatcherKey } from "@infrastructure/eventDispatcher/eventDispatcher";
import { ReviewUpdatedEvent } from "@core/domain/events/reviewUpdated.event";
import { HocuspocusProvider } from "@hocuspocus/provider";
import { TiptapTransformer } from "@hocuspocus/transformer";
import { createDebounce } from "@app/utils/debounce";
import { ReviewItemFileUploaded } from "@core/domain/events/reviewItemFileUploaded";
import { ReviewItem } from "@core/domain/models/reviewItem.model";
import { BackgroundTaskRunner } from "@infrastructure/taskRunner";
import { createReviewItem } from "@core/domain/factories/reviewItem.factory";
import { Highlight } from "@core/domain/models/highlight.model";
import { JobStartedEvent } from "@core/domain/events/jobStarted.event";
import { JobCompletedEvent } from "@core/domain/events/jobCompleted.event";
import { JobFailedEvent } from "@core/domain/events/jobFailed.event";

export const ReviewKey: InjectionKey<Awaited<ReturnType<typeof useReview>>> = Symbol("Review");

export type JobStatus = {
  status: "waiting" | "active" | "failed" | "completed" | "paused";
  attempts?: number;
  error?: string;
};

export type ReviewItemJobStatuses = {
  [reviewItemId: number]: {
    "auto-screen"?: JobStatus;
    "auto-extract"?: JobStatus;
    "pdf-retrieval"?: JobStatus;
  };
};

export default function useReview(reviewId: Id) {
  const review = ref() as Ref<Review>;
  const reviewsService: ReviewsService = injectStrict(ReviewsServiceKey);
  const projectsService = injectStrict(ProjectsServiceKey);
  const eventDispatcher = injectStrict(EventDispatcherKey);
  const evidenceService = injectStrict(EvidenceServiceApiKey);

  const dataExtractionProvider = new HocuspocusProvider({
    url: "/attributes-draft",
    name: "review-" + reviewId + "-data-extraction",
  });

  const previousReviewSynthesisCache = ref<Map<string, string>>(new Map());
  function getReviewSynthesisValue(attributeId: string): string {
    if (!attributeId) return "";

    const field = `review-${reviewId}-synthesis-${attributeId}`;
    const fragment = dataExtractionProvider.document.getXmlFragment(field);

    let text = "";

    fragment.toArray().forEach((node: any) => {
      if (node.text) {
        text += node.text;
      } else if (node.toString) {
        text += node.toString().replaceAll(/<\/?[^>]+>/g, "");
      }
    });

    return text.trim();
  }

  function getPreviousReviewSynthesisValue(attributeId: string): string {
    if (!attributeId) return "";

    const previousReviewVersion = findPreviousReviewVersion(review.value.id);
    if (!previousReviewVersion) return "";

    const currentAttribute = review.value.plan?.synthesisPlan?.attributesStructure.find(
      (attr) => attr.id === attributeId,
    );
    if (!currentAttribute || !currentAttribute.label) return "";

    const cacheKey = `${previousReviewVersion.id}-${currentAttribute.label}`;
    if (previousReviewSynthesisCache.value.has(cacheKey)) {
      return previousReviewSynthesisCache.value.get(cacheKey)!;
    }

    evidenceService.api
      .reviewsControllerGetReviewSynthesis(previousReviewVersion.id, {
        attributeLabel: currentAttribute.label,
        attributeId: "",
      })
      .then((response) => {
        const data = response.data;
        if (data?.text) {
          previousReviewSynthesisCache.value.set(cacheKey, data.text);
        }
      });

    return "";
  }

  function findPreviousReviewVersion(currentVersionId: number):
    | {
        id: number;
        createdAt: Date;
        name: string;
      }
    | undefined {
    const reviewIndex = review.value.reviewSeries.reviews
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      .findIndex((rv) => rv.id === currentVersionId);
    if (reviewIndex > 0) {
      return review.value.reviewSeries.reviews[reviewIndex - 1];
    }
    return undefined;
  }

  onUnmounted(() => dataExtractionProvider.destroy());

  function getDataExtraction() {
    const doc = TiptapTransformer.fromYdoc(dataExtractionProvider.document);
    return doc;
  }

  const reviewItemJobStatuses = ref<ReviewItemJobStatuses>({});

  let skipRefreshUntil = 0;
  const REFRESH_SKIP_DURATION = 2000;

  function withRefreshLock<T extends (...args: any[]) => Promise<any>>(fn: T): T {
    return (async (...args: Parameters<T>) => {
      skipRefreshUntil = Date.now() + REFRESH_SKIP_DURATION;
      try {
        return await fn(...args);
      } finally {
        skipRefreshUntil = Date.now() + REFRESH_SKIP_DURATION;
      }
    }) as T;
  }

  const debounce = createDebounce(1000);
  onMounted(() => {
    eventDispatcher.registerHandler(ReviewUpdatedEvent, (event: ReviewUpdatedEvent) => {
      if (event.reviewId === reviewId && Date.now() >= skipRefreshUntil) {
        debounce(refresh);
      }
    });

    eventDispatcher.registerHandler(JobStartedEvent, (event: JobStartedEvent) => {
      if (event.reviewId === reviewId) {
        if (!reviewItemJobStatuses.value[event.reviewItemId]) {
          reviewItemJobStatuses.value[event.reviewItemId] = {};
        }
        reviewItemJobStatuses.value[event.reviewItemId][
          event.operation as keyof ReviewItemJobStatuses[number]
        ] = {
          status: "active",
        };
      }
    });

    eventDispatcher.registerHandler(JobCompletedEvent, async (event: JobCompletedEvent) => {
      if (event.reviewId === reviewId) {
        if (!reviewItemJobStatuses.value[event.reviewItemId]) {
          reviewItemJobStatuses.value[event.reviewItemId] = {};
        }
        reviewItemJobStatuses.value[event.reviewItemId][
          event.operation as keyof ReviewItemJobStatuses[number]
        ] = {
          status: "completed",
        };
        setTimeout(() => {
          if (reviewItemJobStatuses.value[event.reviewItemId]) {
            delete reviewItemJobStatuses.value[event.reviewItemId][
              event.operation as keyof ReviewItemJobStatuses[number]
            ];
          }
        }, 5000);
        if (
          event.operation === "auto-extract" ||
          event.operation === "auto-synthesis" ||
          event.operation === "auto-synthesis-delta"
        ) {
          await refresh();
        }
      }
    });

    eventDispatcher.registerHandler(JobFailedEvent, (event: JobFailedEvent) => {
      if (event.reviewId === reviewId) {
        if (!reviewItemJobStatuses.value[event.reviewItemId]) {
          reviewItemJobStatuses.value[event.reviewItemId] = {};
        }
        reviewItemJobStatuses.value[event.reviewItemId][
          event.operation as keyof ReviewItemJobStatuses[number]
        ] = {
          status: "failed",
          error: event.error,
          attempts: event.attempts,
        };
      }
    });

    let lastRefreshTime: number | null = null;
    let isRefreshing = false;

    eventDispatcher.registerHandler(
      ReviewItemFileUploaded,
      async (event: ReviewItemFileUploaded) => {
        if (event.reviewId === reviewId && !isRefreshing) {
          const currentTime = Date.now();

          if (lastRefreshTime === null || currentTime - lastRefreshTime >= 10000) {
            isRefreshing = true;
            await refresh();
            lastRefreshTime = Date.now();
            isRefreshing = false;
          }
        }
      },
    );
  });

  const medicalAlternativeNames = ref<string[]>([]);

  async function fetchMedicalAlternativeNames() {
    try {
      const response =
        await evidenceService.api.reviewsControllerGetMedicalAlternativeNames(reviewId);
      medicalAlternativeNames.value = response.data;
    } catch (e) {
      console.error("Failed to fetch medical alternative names", e);
    }
  }

  async function refresh() {
    const foundReview = await reviewsService.findById(reviewId);
    if (!foundReview) throw new Error("review not found");

    const project = await reviewsService.findProject(reviewId);
    review.value = new Review({ ...foundReview, project });

    await fetchActiveJobs();
    await fetchMedicalAlternativeNames();
  }

  async function fetchActiveJobs() {
    try {
      const response = await evidenceService.api.jobsControllerGetJobs({
        reviewId: reviewId.toString(),
        status: "waiting,active,failed,paused",
        pageSize: "1000",
      } as any);

      const jobs = response.data?.jobs || [];
      jobs.forEach((job) => {
        if (!job.reviewItemId) return;

        if (!reviewItemJobStatuses.value[job.reviewItemId]) {
          reviewItemJobStatuses.value[job.reviewItemId] = {};
        }

        reviewItemJobStatuses.value[job.reviewItemId][
          job.operation as keyof ReviewItemJobStatuses[number]
        ] = {
          status: job.status as JobStatus["status"],
          error: job.error,
          attempts: job.attempts,
        };
      });
    } catch (error) {
      console.error("Failed to fetch active jobs:", error);
    }
  }

  const filtering = useStudiesFiltering();
  const sorting = useStudiesSorting();

  const searchesBySource = computed<{ [key: string]: Search[] }>(() => {
    const builtInSources = Object.values(BuiltInImportSourceId);

    const plannedSources =
      review.value.plan?.importPlan.importSources
        ?.map((source) => source.id)
        .filter((id) => !builtInSources.includes(id as BuiltInImportSourceId)) ?? [];

    const allSources = [...builtInSources, ...plannedSources];

    return allSources.reduce(
      (acc, sourceId) => {
        acc[sourceId] = review.value?.searches?.filter((s) => s.source.id === sourceId) ?? [];
        return acc;
      },
      {} as { [key: string]: Search[] },
    );
  });

  const areInvalidStudiesDisplayed = ref(false);

  const isPlanReadonly = computed(
    () => review.value.plan?.lockState === ReviewLockState.LOCKED || review.value.isLocked,
  );

  const isLocked = computed(() => review.value.isLocked);

  const isReviewReadonly = computed(
    () => review.value.isLocked || review.value.plan?.lockState === ReviewLockState.UNLOCKED,
  );
  const inclusionCriteria = computed(() => review.value.plan?.inclusionCriteria);

  const worker = new Worker(new URL("./workers/processReviewItem.worker.ts", import.meta.url), {
    type: "module",
  });

  onUnmounted(() => worker.terminate());
  const taskRunner = new BackgroundTaskRunner(worker);

  const parentStudyIds = computed(() => {
    return new Set(
      review.value?.studies.map((study) => study.parentStudyId).filter((id): id is number => !!id),
    );
  });

  const maybeParentStudyIds = computed(() => {
    return new Set(
      review.value?.studies
        .map((study) => study.maybeParentStudyId)
        .filter((id): id is number => !!id),
    );
  });

  const filteredReviewItems = ref<ReviewItem[]>([]);

  const filteredIncludedReviewItems = ref<ReviewItem[]>([]);
  async function processReviewItems() {
    if (!review.value?.studies?.length) return;

    const jsonItems: ReviewItem[] = await taskRunner.runTask(
      "processReviewItems",
      JSON.parse(
        JSON.stringify({
          reviewItems: review.value.studies,
          terms: filtering.highlightOnly.value ? [] : filtering.terms.value,
          states: Object.keys(filtering.filters.value).filter(
            (f) => !!filtering.filters.value[f as StudyState],
          ) as StudyState[],
          sortBy: sorting.sort.value.by,
          sortOrder: sorting.sort.value.order,
          parentStudyIds: parentStudyIds.value,
          maybeParentStudyIds: maybeParentStudyIds.value,
          filters: filtering.filters.value,
          sourceFilter: filtering.sourceFilter.value,
          hasPdfFilter: filtering.hasPdfFilter.value,
          titleFilter: filtering.titleFilter.value,
          authorFilter: filtering.authorFilter.value,
          hasScreeningSuggestionFilter: filtering.hasScreeningSuggestionFilter.value,
          hasExtractionSuggestionFilter: filtering.hasExtractionSuggestionFilter.value,
        }),
      ),
    );
    filteredReviewItems.value = jsonItems.map((i: any) => createReviewItem(i));
  }
  const includedReviewItems = ref<ReviewItem[]>([]);

  async function processIncludedReviewItems() {
    if (!review.value?.studies?.length) return;
    await Promise.allSettled([
      taskRunner
        .runTask(
          "processIncludedReviewItems",
          JSON.parse(
            JSON.stringify({
              reviewItems: review.value.studies,
              sortBy: sorting.sort.value.by,
              sortOrder: sorting.sort.value.order,
            }),
          ),
        )
        .then((items) => {
          includedReviewItems.value = (items as ReviewItem[]).map((i: any) => createReviewItem(i));
        }),
      taskRunner
        .runTask(
          "processFilteredIncludedReviewItems",
          JSON.parse(
            JSON.stringify({
              reviewItems: review.value.studies,
              terms: filtering.highlightOnly.value ? [] : filtering.terms.value,
              sortBy: sorting.sort.value.by,
              sortOrder: sorting.sort.value.order,
              sourceFilter: filtering.sourceFilter.value,
              hasPdfFilter: filtering.hasPdfFilter.value,
              titleFilter: filtering.titleFilter.value,
              authorFilter: filtering.authorFilter.value,
              hasScreeningSuggestionFilter: filtering.hasScreeningSuggestionFilter.value,
              hasExtractionSuggestionFilter: filtering.hasExtractionSuggestionFilter.value,
            }),
          ),
        )
        .then((items) => {
          filteredIncludedReviewItems.value = (items as ReviewItem[]).map((i: any) =>
            createReviewItem(i),
          );
        }),
    ]);
  }
  watch(
    [
      () => filtering.highlightOnly.value,
      () => filtering.terms.value,
      () => sorting.sort.value,
      () => filtering.filters.value,
      () => filtering.sourceFilter.value,
      () => filtering.hasPdfFilter.value,
      () => filtering.titleFilter.value,
      () => filtering.authorFilter.value,
      () => filtering.hasScreeningSuggestionFilter.value,
      () => filtering.hasExtractionSuggestionFilter.value,
    ],
    async () => {
      await processReviewItems();
      await processIncludedReviewItems();
    },
    { immediate: true, deep: true },
  );

  watch(
    [() => review.value?.studies],
    async () => {
      await processReviewItems();
      await processIncludedReviewItems();
    },
    { immediate: true, deep: true },
  );

  const markStudyAsDuplicate = async (studyId: Id, parentStudyId: Id): Promise<void> => {
    if (!review.value) throw Error("search not found");
    await reviewsService.markStudyAsDuplicate(review.value.id, studyId, parentStudyId);
    const study = review.value.studies.find((s) => s.id === studyId)!;
    study.markAsDuplicate(parentStudyId);
  };

  const markStudyAsNotDuplicate = async (studyId: Id): Promise<void> => {
    if (!review.value) throw Error("search not found");
    await reviewsService.markStudyAsNotDuplicate(review.value?.id, studyId);
    const study = review.value.studies.find((s) => s.id === studyId)!;
    study.markAsNotDuplicate();
  };

  const setStudyTitleAndAbstractScreening = async (studyId: Id, key: string) => {
    if (!review.value) throw Error("review not found");
    await reviewsService.setStudyTitleAndAbstractScreening(review.value.id, studyId, key);
    await refresh();
  };

  const clearStudyTitleAndAbstractScreening = async (studyId: Id) => {
    if (!review.value) throw Error("search not found");
    await reviewsService.clearStudyTitleAndAbstractScreening(review.value.id, studyId);
    await refresh();
  };

  const setStudyFullTextScreening = async (studyId: Id, fullTextScreening: string) => {
    if (!review.value) throw Error("search not found");
    await reviewsService.setStudyFullTextScreening(review.value.id, studyId, fullTextScreening);
    await refresh();
  };

  const clearStudyFullTextScreening = async (studyId: Id) => {
    if (!review.value) throw Error("search not found");
    await reviewsService.clearStudyFullTextScreening(review.value.id, studyId);
    await refresh();
  };

  const favoriteStudy = async (studyId: Id) => {
    if (!review.value) throw new Error("no search set");
    await reviewsService.favoriteStudy(review.value.id, studyId);
    await refresh();
  };

  const unfavoriteStudy = async (studyId: Id) => {
    if (!review.value) throw new Error("no search set");
    await reviewsService.unfavoriteStudy(review.value.id, studyId);
    await refresh();
  };

  const removeSearch = async (searchId: number) => {
    if (!review.value) throw new Error("no review set");
    await reviewsService.removeSearch(review.value.id, searchId);
    review.value.searches = review.value.searches?.filter((s) => s.id !== searchId);
  };

  async function uploadStudyPdfFile(studyId: Id, file: File) {
    if (!review.value) throw new Error("no review set");
    const pdfFile = await reviewsService.uploadStudyPdfFile(review.value.id, studyId, file);
    const study = review.value.studies.find((s) => s.id === studyId)!;
    review.value.updateStudy(studyId, {
      ...study,
      pdfFile,
    });
  }

  async function getStudyPdfFile(studyId: Id) {
    if (!review.value) throw new Error("no review set");
    return reviewsService.getStudyPdfFile(review.value.id, studyId);
  }

  async function downloadSearchCitationFile(searchId: Id) {
    if (!review.value) throw new Error("no review set");
    return reviewsService.downloadSearchCitationFile(review.value.id, searchId);
  }

  async function deleteStudyPdfFile(studyId: Id) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.deleteStudyPdfFile(review.value.id, studyId);
    review.value.updateStudy(studyId, {
      pdfFile: undefined,
    });
  }

  async function downloadPdfZip() {
    if (!review.value) throw new Error("no review set");
    return reviewsService.downloadPdfZip(review.value.id);
  }

  async function getArticlePdfPrice(studyId: Id, doi?: string): Promise<number> {
    return reviewsService.getArticlePdfPrice(studyId, doi);
  }

  async function createArticlePdfOrder(studyId: Id, doi: string): Promise<void> {
    return reviewsService.createArticlePdfOrder(studyId, {
      doi,
      reviewId: review.value.id,
    });
  }

  async function editStudyAbstract(studyId: Id, abstract: string) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.editStudyAbstract(review.value.id, studyId, abstract);
    await refresh();
  }

  async function submitComplaint(
    studyId: Id,
    data: {
      body: string;
      fromAddress: string;
      fromName: string;
      subject: string;
      to: string;
    },
  ) {
    if (!review.value) throw new Error("no review set");
    const complaintDate = await reviewsService.submitComplaint(review.value.id, studyId, data);

    review.value.updateStudy(studyId, {
      complaintDate,
    });
  }

  async function addFullTextCriterion(criterion: string) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.addFullTextCriterion(review.value.id, criterion);
    review.value.plan?.screeningPlan.fullTextCriteria.push(criterion);
  }

  async function enableTitleAndAbstractScreening() {
    if (!review.value) throw new Error("no review set");
    await reviewsService.enableTitleAndAbstractScreening(review.value.id);
  }

  async function disableTitleAndAbstractScreening() {
    if (!review.value) throw new Error("no review set");
    await reviewsService.disableTitleAndAbstractScreening(review.value.id);
    if (review.value.plan) review.value.plan.screeningPlan.titleAndAbstractCriteria = [];
  }

  async function deletFullTextCriterion(criterion: string) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.deleteFullTextCriterion(review.value.id, criterion);
    if (review.value.plan)
      review.value.plan.screeningPlan.fullTextCriteria =
        review.value.plan?.screeningPlan.fullTextCriteria.filter((c) => c !== criterion);
  }

  async function addTitleAndAbstractCriterion(criterion: string) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.addTitleAndAbstractCriterion(review.value.id, criterion);
    review.value.plan?.screeningPlan.titleAndAbstractCriteria.push(criterion);
  }

  async function deleteTitleAndAbstractCriterion(criterion: string) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.deleteTitleAndAbstractCriterion(review.value.id, criterion);
    if (review.value.plan)
      review.value.plan.screeningPlan.titleAndAbstractCriteria =
        review.value.plan?.screeningPlan.titleAndAbstractCriteria.filter((c) => c !== criterion);
  }

  async function removeImportSourceFromPlan(importSourceId: string) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.removeImportSourceFromPlan(review.value.id, importSourceId);
    review.value.removeImportSource(importSourceId);
  }

  async function enableStudiesAppraisalImdrfMdce2019() {
    if (!review.value) throw new Error("no review set");
    if (!review.value.plan?.appraisalPlan) throw new Error("");
    await reviewsService.enableStudiesAppraisalImdrfMdce2019(review.value.id);
    review.value.plan.appraisalPlan.isImdrfMdce2019Applicable = true;
  }

  async function enableStudiesAppraisalOxfordLevelOfEvidence() {
    if (!review.value) throw new Error("no review set");
    if (!review.value.plan?.appraisalPlan) throw new Error("");
    await reviewsService.enableStudiesAppraisalOxfordLevelOfEvidence(review.value.id);
    review.value.plan.appraisalPlan.isOxfordLevelOfEvidenceApplicable = true;
  }
  async function enablePicoInclusionCriteria() {
    if (!review.value) throw new Error("no review set");
    if (!review.value.plan?.appraisalPlan) throw new Error("");
    await reviewsService.enablePicoInclusionCriteria(review.value.id);
    review.value.plan.inclusionCriteria.isPicoInclusionCriteriaApplicable = true;
  }

  async function disablePicoInclusionCriteria() {
    if (!review.value) throw new Error("no review set");
    if (!review.value.plan?.appraisalPlan) throw new Error("");
    await reviewsService.disablePicoInclusionCriteria(review.value.id);
    review.value.plan.inclusionCriteria.isPicoInclusionCriteriaApplicable = false;
  }

  async function enableEvidenceLevelInclusionCriteria() {
    if (!review.value) throw new Error("no review set");
    if (!review.value.plan?.inclusionCriteria) throw new Error("");
    await reviewsService.enableEvidenceLevelInclusionCriteria(review.value.id);
    review.value.plan.inclusionCriteria.isEvidenceLevelEnabled = true;
  }

  async function disableEvidenceLevelInclusionCriteria() {
    if (!review.value) throw new Error("no review set");
    if (!review.value.plan?.inclusionCriteria) throw new Error("");
    await reviewsService.disableEvidenceLevelInclusionCriteria(review.value.id);
    review.value.plan.inclusionCriteria.isEvidenceLevelEnabled = false;
  }

  async function enableTimeLimtsInclusionCriteria() {
    if (!review.value) throw new Error("no review set");
    if (!review.value.plan?.inclusionCriteria) throw new Error("");
    await reviewsService.enableTimeLimtsInclusionCriteria(review.value.id);
    review.value.plan.inclusionCriteria.isTimeLimitEnabled = true;
  }

  async function disableTimeLimtsInclusionCriteria() {
    if (!review.value) throw new Error("no review set");
    if (!review.value.plan?.inclusionCriteria) throw new Error("");
    await reviewsService.disableTimeLimtsInclusionCriteria(review.value.id);
    review.value.plan.inclusionCriteria.isTimeLimitEnabled = false;
  }

  async function updateInclusionCriteriaEvidenceLevel(levelOfEvidence: string) {
    if (!review.value) throw new Error("no review set");
    if (!review.value.plan?.inclusionCriteria) throw new Error("");
    await reviewsService.updateInclusionCriteriaEvidenceLevel(review.value.id, levelOfEvidence);
    review.value.plan.inclusionCriteria.evidenceLevel = levelOfEvidence;
  }

  async function updateInclusionCriteriaTimeLimit({
    timeLimit,
    timeLimitFromDate,
    timeLimitToDate,
  }: {
    timeLimit: number;
    timeLimitFromDate: string;
    timeLimitToDate: string;
  }) {
    if (!review.value) throw new Error("no review set");
    if (!review.value.plan?.inclusionCriteria) throw new Error("");
    await reviewsService.updateInclusionCriteriaTimeLimit({
      reviewId: review.value.id,
      timeLimit: timeLimit,
      timeLimitFromDate,
      timeLimitToDate,
    });
    review.value.plan.inclusionCriteria.timeLimit = timeLimit;
  }

  async function enableStudiesAppraisalPeerReviewStatus() {
    if (!review.value) throw new Error("no review set");
    if (!review.value.plan?.appraisalPlan) throw new Error("");
    await reviewsService.enableStudiesAppraisalPeerReviewStatus(review.value.id);
    review.value.plan.appraisalPlan.isPeerReviewStatusApplicable = true;
  }

  async function disableStudiesAppraisal() {
    if (!review.value) throw new Error("no review set");
    if (!review.value.plan?.appraisalPlan) throw new Error("");
    await reviewsService.disableStudiesAppraisal(review.value.id);
    review.value.plan.appraisalPlan.isImdrfMdce2019Applicable = false;
  }

  async function disableStudiesAppraisalOxfordLevelOfEvidence() {
    if (!review.value) throw new Error("no review set");
    if (!review.value.plan?.appraisalPlan) throw new Error("");
    await reviewsService.disableStudiesAppraisalOxfordLevelOfEvidence(review.value.id);
    review.value.plan.appraisalPlan.isOxfordLevelOfEvidenceApplicable = false;
  }

  async function disableStudiesAppraisalPeerReviewStatus() {
    if (!review.value) throw new Error("no review set");
    if (!review.value.plan?.appraisalPlan) throw new Error("");
    await reviewsService.disableStudiesAppraisalPeerReviewStatus(review.value.id);
    review.value.plan.appraisalPlan.isPeerReviewStatusApplicable = false;
  }

  async function enableTextualSynthesis() {
    if (!review.value) throw new Error("no review set");
    if (!review.value.plan?.synthesisPlan) throw new Error("");
    await reviewsService.enableTextualSynthesis(review.value.id);
    review.value.plan.synthesisPlan.isTextualSynthesisEnabled = true;
  }

  async function disableTextualSynthesis() {
    if (!review.value) throw new Error("no review set");
    if (!review.value.plan?.synthesisPlan) throw new Error("");
    await reviewsService.disableTextualSynthesis(review.value.id);
    review.value.plan.synthesisPlan.isTextualSynthesisEnabled = false;
  }

  async function appraiseStudy(studyId: Id, appraisal: { criterionId: string; gradeId: string }) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.appraiseStudy(review.value?.id, studyId, appraisal);
    await refresh();
  }
  async function setOxfordLevelOfEvidenceStudy(
    studyId: Id,
    oxfordLevelOfEvidence: OxfordLevelOfEvidenceType,
  ) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.setOxfordLevelOfEvidenceStudy(
      review.value?.id,
      studyId,
      oxfordLevelOfEvidence,
    );
    await refresh();
  }

  async function setStudyPeerReviewStatus(studyId: Id, peerReviewStatus: PeerReviewStatus) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.setStudyPeerReviewStatus(review.value?.id, studyId, peerReviewStatus);
    review.value.updateStudy(studyId, {
      peerReviewStatus: peerReviewStatus,
    });
  }

  async function clearStudyAppraisal(studyId: Id, criterionId: string) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.clearStudiesAppraisal(review.value.id, studyId, criterionId);
    await refresh();
  }

  async function clearStudyOxfordLevelOfEvidence(studyId: Id) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.cleaStudyOxfordLevelOfEvidence(review.value.id, studyId);
    await refresh();
  }

  async function clearStudyPeerReviewStatus(studyId: Id) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.clearStudyPeerReviewStatus(review.value.id, studyId);
    review.value.updateStudy(studyId, {
      peerReviewStatus: PeerReviewStatus.default,
    });
  }

  async function updateStudy(studyId: Id, metadata: MetaData) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.updateStudy(review.value.id, studyId, metadata);
    await refresh();
  }

  async function addAttributeStructure(reviewId: Id, attribute: Partial<AttributeStructure>) {
    const attr = await reviewsService.addAttributeStructure(reviewId, {
      label: attribute.label ?? "",
      question: attribute.question ?? "",
      order: attribute.order ?? "",
      type: attribute.type,
    });
    if (review.value.plan) {
      review.value.plan.synthesisPlan.attributesStructure.push(attr);
    }
  }

  async function deleteAttributeStructure(reviewId: Id, attributeStructureId: string) {
    await reviewsService.deleteAttributeStructure(reviewId, attributeStructureId);
    if (review.value.plan) {
      review.value.plan.synthesisPlan.attributesStructure =
        review.value.plan.synthesisPlan.attributesStructure.filter(
          (a) => a.id !== attributeStructureId,
        );
    }
  }

  async function editAttributeStructure(reviewId: Id, updatedAttribute: AttributeStructure) {
    await reviewsService.editAttributeStructure(reviewId, updatedAttribute);
    const foundAttributeIndex =
      review.value.plan?.synthesisPlan.attributesStructure?.findIndex(
        (a) => a.id === updatedAttribute.id,
      ) ?? -1;
    if (foundAttributeIndex !== -1) {
      review.value.plan!.synthesisPlan.attributesStructure[foundAttributeIndex] = updatedAttribute;
    }
  }

  async function lockPlan(reviewId: Id) {
    await reviewsService.lockPlan(reviewId);
    if (review.value.plan) review.value.plan.lockState = ReviewLockState.LOCKED;
  }

  async function unlockPlan(reviewId: Id) {
    await reviewsService.unlockPlan(reviewId);
    if (review.value.plan) review.value.plan.lockState = ReviewLockState.UNLOCKED;
  }

  async function addImportSourceToPlan(data: {
    id?: string;
    name: string;
    url: string;
    type: ImportSourceType;
    fullName?: string;
    description?: string;
  }): Promise<string> {
    const sourceId = await reviewsService.addImportSourceToPlan(reviewId, data);
    await refresh();
    return sourceId;
  }

  async function importRisSearch({
    citationFiles,
    date,
    query,
    filters,
    importSourceId,
  }: {
    query?: string;
    filters?: string;
    citationFiles: File[];
    date: string;
    importSourceId: string;
  }) {
    await reviewsService.importRisSearch({
      reviewId,
      citationFiles,
      date,
      query,
      filters,
      importSourceId,
    });
    await refresh();
  }

  async function importSearch({
    query,
    filters,
    date,
    importSourceId,
    items,
    groupId,
    url,
  }: {
    query?: string;
    filters?: string;
    date: string;
    importSourceId: string;
    items: any[];
    groupId?: string;
    url?: string;
  }) {
    await reviewsService.importSearch({
      reviewId,
      query,
      filters,
      date,
      importSourceId,
      items,
      groupId,
      url,
    });
    await refresh();
  }

  async function importCitationSearch({
    citationFiles,
    parentStudyId,
  }: {
    citationFiles: File[];
    parentStudyId: number;
  }) {
    await reviewsService.importCitationSearch({
      reviewId,
      citationFiles,
      parentStudyId,
    });
    await refresh();
  }

  const sortedAttributes = computed(
    () =>
      review.value.plan!.synthesisPlan?.attributesStructure.toSorted((a, b) => {
        if (a.order === null && b.order === null) return 0;
        if (a.order === null) return -1;
        if (b.order === null) return 1;
        return a.order > b.order ? 1 : -1;
      }) ?? [],
  );

  async function answerStudyQuestion(studyId: number, attributeId: string): Promise<void> {
    if (!review.value) throw new Error("no review set");

    await evidenceService.api.reviewItemsControllerAnswerStudyQuestion(studyId, attributeId);
    await refresh();
  }

  async function addPicoInclusionCriterion(newCriterion: {
    criterionType: InclusionCriterion;
    criterion: string;
  }) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.addPicoInclusionCriterion(review.value.id, newCriterion);
    review.value.plan?.inclusionCriteria[newCriterion.criterionType].push(newCriterion.criterion);
  }

  async function deletePicoInclusionCriterion(criterionDetails: {
    criterionType: InclusionCriterion;
    criterion: string;
  }) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.deletePicoInclusionCriterion(review.value.id, criterionDetails);
    if (review.value.plan?.inclusionCriteria[criterionDetails.criterionType])
      review.value.plan.inclusionCriteria[criterionDetails.criterionType] =
        review.value.plan?.inclusionCriteria[criterionDetails.criterionType].filter(
          (c) => c !== criterionDetails.criterion,
        );
  }

  async function addInclusionCriterion(criterion: string) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.addInclusionCriterion(review.value.id, criterion);
    review.value.plan?.inclusionCriteria.criteria.push(criterion);
  }

  async function deleteInclusionCriterion(criterion: string) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.deleteInclusionCriterion(review.value.id, criterion);
    if (review.value.plan?.inclusionCriteria.criteria)
      review.value.plan.inclusionCriteria.criteria =
        review.value.plan?.inclusionCriteria.criteria.filter((c) => c !== criterion);
  }

  async function addAppraisalCriterionAnswer(data: {
    appraisalCriterionId: string;
    answer: string;
  }) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.addAppraisalCriteria(review.value.id, data);
    await refresh();
  }
  async function deleteAppraisalCriterionAnswer(data: {
    answerId: string;
    appraisalCriterionId: string;
  }) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.deleteAppraisalCriterionAnswer(review.value.id, data);
    const criterion = review.value.plan?.appraisalCriteria?.find(
      (c) => c.id === data.appraisalCriterionId,
    );
    if (criterion) {
      criterion.answers = criterion.answers.filter((a) => a.id !== data.answerId);
    }
  }
  async function lockReview(reviewId: Id) {
    await reviewsService.lock(reviewId);
    review.value.isLocked = true;
  }

  async function unlockReview(reviewId: Id) {
    await reviewsService.unlock(reviewId);
    review.value.isLocked = false;
  }

  async function updateCslStyle(cslStyle: string) {
    if (!review.value.project?.id) throw new Error("something went wrong, cannot find project id");
    await projectsService.updateCslStyle(review.value.project?.id, cslStyle);
    review.value.project.cslStyle = cslStyle;
  }

  async function finalizeReviewItemSynthesisAttribute(studyId: Id, attributeId: string) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.finalizeReviewItemSynthesisAttribute(
      review.value?.id,
      studyId,
      attributeId,
    );

    await refresh();
  }

  async function unfinalizeReviewItemSynthesisAttribute(studyId: Id, attributeId: string) {
    if (!review.value) throw new Error("no review set");
    await reviewsService.unfinalizeReviewItemSynthesisAttribute(
      review.value?.id,
      studyId,
      attributeId,
    );

    await refresh();
  }

  async function clearAttributeAiSuggestion(studyId: Id, attributeId: string) {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.reviewsControllerClearAttributeAiSuggestion(
      review.value.id,
      studyId,
      { id: attributeId },
    );

    await refresh();
  }

  async function editAttributeAiSuggestion(studyId: Id, attributeId: string) {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.reviewsControllerEditAttributeAiSuggestion(review.value.id, studyId, {
      id: attributeId,
    });

    await refresh();
  }

  async function saveMedicalAlternativesAttributeValue(
    reviewItemId: Id,
    attributeStructureId: string,
    value: SaveMedicalAlternativesAttributeValueDto,
  ) {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.reviewsControllerSaveMedicalAlternativesAttributeValue(
      review.value.id,
      reviewItemId,
      attributeStructureId,
      value,
    );

    await refresh();
  }

  async function updateSynthesisPlanSummary(summary: string) {
    await reviewsService.updateSynthesisPlanSummary(review.value.id, summary);
  }

  async function updateTextualSynthesisDescription(textualSynthesisDescription: string) {
    await reviewsService.updateTextualSynthesisDescription(
      review.value.id,
      textualSynthesisDescription,
    );
  }

  async function updateSearchStrategySummary(strategy: string) {
    await reviewsService.updateSearchStrategySummary(review.value.id, strategy);
  }

  async function createEvaluator({
    name,
    role,
    order,
  }: {
    name: string;
    role: string;
    order: string;
  }) {
    const evaluatorId = await reviewsService.createEvaluator({
      name,
      role,
      order,
      reviewId: review.value.id,
    });
    review.value.evaluators.push({
      id: evaluatorId,
      name,
      role,
      order,
    });
  }

  async function updateEvaluator({
    id,
    name,
    role,
    order,
  }: {
    id: number;
    role: string;
    name: string;
    order: string;
  }) {
    await reviewsService.updateEvaluator({
      reviewId: review.value.id,
      evaluatorId: id,
      name,
      role,
      order,
    });
    review.value.evaluators = review.value.evaluators.map((evaluator) => {
      if (evaluator.id === id) {
        return {
          id,
          name,
          role,
          order,
        };
      }
      return evaluator;
    });
  }

  async function deleteEvaluator(id: number) {
    await reviewsService.deleteEvaluator({
      evaluatorId: id,
      reviewId: review.value.id,
    });
    review.value.evaluators = review.value.evaluators.filter((evaluator) => evaluator.id !== id);
  }

  async function pauseJobs() {
    if (!review.value) throw new Error("no review set");
    await Promise.all([
      evidenceService.api.jobsControllerPauseAutoScreening(review.value.id.toString()),
      evidenceService.api.jobsControllerPauseAutoExtraction(review.value.id.toString()),
    ]);
    await refresh();
  }

  async function resumeJobs() {
    if (!review.value) throw new Error("no review set");
    await Promise.all([
      evidenceService.api.jobsControllerResumeAutoScreening(review.value.id.toString()),
      evidenceService.api.jobsControllerResumeAutoExtraction(review.value.id.toString()),
    ]);
    await refresh();
  }

  async function pauseAutoScreening() {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.jobsControllerPauseAutoScreening(review.value.id.toString());
    await refresh();
  }

  async function resumeAutoScreening() {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.jobsControllerResumeAutoScreening(review.value.id.toString());
    await refresh();
  }

  async function pauseAutoExtraction() {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.jobsControllerPauseAutoExtraction(review.value.id.toString());
    await refresh();
  }

  async function resumeAutoExtraction() {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.jobsControllerResumeAutoExtraction(review.value.id.toString());
    await refresh();
  }

  async function pauseAutoSynthesis() {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.jobsControllerPauseAutoSynthesis(review.value.id.toString());
    await refresh();
  }

  async function resumeAutoSynthesis() {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.jobsControllerResumeAutoSynthesis(review.value.id.toString());
    await refresh();
  }

  async function pauseAutoSynthesisDelta() {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.jobsControllerPauseAutoSynthesisDelta(review.value.id.toString());
    await refresh();
  }

  async function resumeAutoSynthesisDelta() {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.jobsControllerResumeAutoSynthesisDelta(review.value.id.toString());
    await refresh();
  }

  async function acceptAllAutoScreeningSuggestions() {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.reviewsControllerAcceptAllScreeningSuggestions(review.value.id);
    await refresh();
  }

  async function addAttributeHighlight({
    highlight,
    reviewItemId,
    attributeStructureId,
  }: {
    reviewItemId: number;
    attributeStructureId: string;
    highlight: {
      pageNumber: number;
      path: string;
      text: string;
      color?: string;
    };
  }) {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.reviewItemsControllerAddHighlight(reviewItemId, {
      reviewId: review.value.id,
      attributeStructureId: attributeStructureId,
      highlight,
    });
  }

  async function deleteHighlight({
    highlight,
    reviewItemId,
    attributeStructureId,
  }: {
    reviewItemId: number;
    attributeStructureId: string;
    highlight: Highlight;
  }) {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.reviewItemsControllerDeleteHighlight(reviewItemId, {
      reviewId: review.value.id,
      attributeStructureId: attributeStructureId,
      highlightId: highlight.id,
    });
  }

  async function getAttributeHighlights(
    reviewItemId: number,
    attributeId: string,
  ): Promise<Highlight[]> {
    if (!review.value) throw new Error("no review set");
    const { data } = await evidenceService.api.reviewItemsControllerGetAttributeHighlights(
      reviewItemId,
      attributeId,
    );
    return data.map((a) => {
      return {
        id: a.id,
        pageNumber: a.pageNumber,
        path: a.path,
        text: a.text,
        color: a.color,
      };
    });
  }

  async function addScreeningHighlight({
    highlight,
    reviewItemId,
  }: {
    reviewItemId: number;
    highlight: Omit<Highlight, "id"> & { id?: string };
  }) {
    if (!review.value) throw new Error("no review set");

    await evidenceService.api.reviewItemsControllerAddScreeningHighlight(reviewItemId, {
      reviewId: review.value.id,
      highlight: {
        pageNumber: highlight.pageNumber,
        path: highlight.path,
        color: highlight.color,
        text: highlight.text,
      },
    });
  }

  async function deleteScreeningHighlight({
    highlight,
    reviewItemId,
  }: {
    reviewItemId: number;
    highlight: Highlight;
  }) {
    if (!review.value) throw new Error("no review set");

    await evidenceService.api.reviewItemsControllerDeleteScreeningHighlight(reviewItemId, {
      reviewId: review.value.id,
      highlightId: highlight.id,
    });
  }

  async function getScreeningHighlights(reviewItemId: number): Promise<Highlight[]> {
    if (!review.value) throw new Error("no review set");

    const { data } =
      await evidenceService.api.reviewItemsControllerGetScreeningHighlights(reviewItemId);

    return data.map((h: any) => ({
      id: h.id,
      pageNumber: h.pageNumber,
      path: h.path,
      text: h.text,
      color: h.color,
    }));
  }

  async function finalizeAttributeSynthesis(attributeId: string) {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.reviewsControllerFinalizeAttributeSynthesis(
      review.value.id,
      attributeId,
    );
    const attribute = review.value.plan?.synthesisPlan.attributesStructure.find(
      (a) => a.id === attributeId,
    );
    if (attribute) attribute.isSynthesisFinalized = true;
  }

  async function unfinalizeAttributeSynthesis(attributeId: string) {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.reviewsControllerUnfinalizeAttributeSynthesis(
      review.value.id,
      attributeId,
    );
    const attribute = review.value.plan?.synthesisPlan.attributesStructure.find(
      (a) => a.id === attributeId,
    );
    if (attribute) attribute.isSynthesisFinalized = false;
  }

  async function finalizeAttributeSynthesisDelta(attributeId: string) {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.reviewsControllerFinalizeAttributeSynthesisDelta(
      review.value.id,
      attributeId,
    );
    const attribute = review.value.plan?.synthesisPlan.attributesStructure.find(
      (a) => a.id === attributeId,
    );
    if (attribute) attribute.isSynthesisDeltaFinalized = true;
  }

  async function unfinalizeAttributeSynthesisDelta(attributeId: string) {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.reviewsControllerUnfinalizeAttributeSynthesisDelta(
      review.value.id,
      attributeId,
    );
    const attribute = review.value.plan?.synthesisPlan.attributesStructure.find(
      (a) => a.id === attributeId,
    );
    if (attribute) attribute.isSynthesisDeltaFinalized = false;
  }

  async function editSynthesisAiSuggestion(attributeId: string) {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.reviewsControllerEditSynthesisAiSuggestion(
      review.value.id,
      attributeId,
    );
    const attribute = review.value.plan?.synthesisPlan.attributesStructure.find(
      (a) => a.id === attributeId,
    );
    if (attribute) attribute.isSynthesisAiSuggested = false;
  }

  async function clearSynthesisAiSuggestion(attributeId: string) {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.reviewsControllerClearSynthesisAiSuggestion(
      review.value.id,
      attributeId,
    );
    const attribute = review.value.plan?.synthesisPlan.attributesStructure.find(
      (a) => a.id === attributeId,
    );
    if (attribute) attribute.isSynthesisAiSuggested = false;
  }

  async function editSynthesisDeltaAiSuggestion(attributeId: string) {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.reviewsControllerEditSynthesisDeltaAiSuggestion(
      review.value.id,
      attributeId,
    );
    const attribute = review.value.plan?.synthesisPlan.attributesStructure.find(
      (a) => a.id === attributeId,
    );
    if (attribute) attribute.isSynthesisDeltaAiSuggested = false;
  }

  async function clearSynthesisDeltaAiSuggestion(attributeId: string) {
    if (!review.value) throw new Error("no review set");
    await evidenceService.api.reviewsControllerClearSynthesisDeltaAiSuggestion(
      review.value.id,
      attributeId,
    );
    const attribute = review.value.plan?.synthesisPlan.attributesStructure.find(
      (a) => a.id === attributeId,
    );
    if (attribute) attribute.isSynthesisDeltaAiSuggested = false;
  }

  return {
    refresh,
    finalizeAttributeSynthesis: withRefreshLock(finalizeAttributeSynthesis),
    unfinalizeAttributeSynthesis: withRefreshLock(unfinalizeAttributeSynthesis),
    finalizeAttributeSynthesisDelta: withRefreshLock(finalizeAttributeSynthesisDelta),
    unfinalizeAttributeSynthesisDelta: withRefreshLock(unfinalizeAttributeSynthesisDelta),
    editSynthesisAiSuggestion: withRefreshLock(editSynthesisAiSuggestion),
    clearSynthesisAiSuggestion: withRefreshLock(clearSynthesisAiSuggestion),
    editSynthesisDeltaAiSuggestion: withRefreshLock(editSynthesisDeltaAiSuggestion),
    clearSynthesisDeltaAiSuggestion: withRefreshLock(clearSynthesisDeltaAiSuggestion),
    markStudyAsDuplicate: withRefreshLock(markStudyAsDuplicate),
    markStudyAsNotDuplicate: withRefreshLock(markStudyAsNotDuplicate),
    setStudyTitleAndAbstractScreening: withRefreshLock(setStudyTitleAndAbstractScreening),
    clearStudyTitleAndAbstractScreening: withRefreshLock(clearStudyTitleAndAbstractScreening),
    setStudyFullTextScreening: withRefreshLock(setStudyFullTextScreening),
    clearStudyFullTextScreening: withRefreshLock(clearStudyFullTextScreening),
    removeSearch: withRefreshLock(removeSearch),
    enableStudiesAppraisalOxfordLevelOfEvidence: withRefreshLock(
      enableStudiesAppraisalOxfordLevelOfEvidence,
    ),
    removeImportSourceFromPlan: withRefreshLock(removeImportSourceFromPlan),
    favoriteStudy: withRefreshLock(favoriteStudy),
    unfavoriteStudy: withRefreshLock(unfavoriteStudy),
    uploadStudyPdfFile: withRefreshLock(uploadStudyPdfFile),
    getStudyPdfFile,
    deleteStudyPdfFile: withRefreshLock(deleteStudyPdfFile),
    downloadPdfZip,
    downloadSearchCitationFile,
    editStudyAbstract: withRefreshLock(editStudyAbstract),
    enableStudiesAppraisalImdrfMdce2019: withRefreshLock(enableStudiesAppraisalImdrfMdce2019),
    disableStudiesAppraisalOxfordLevelOfEvidence: withRefreshLock(
      disableStudiesAppraisalOxfordLevelOfEvidence,
    ),
    enableStudiesAppraisalPeerReviewStatus: withRefreshLock(enableStudiesAppraisalPeerReviewStatus),
    disableStudiesAppraisalPeerReviewStatus: withRefreshLock(
      disableStudiesAppraisalPeerReviewStatus,
    ),
    enableTextualSynthesis: withRefreshLock(enableTextualSynthesis),
    disableTextualSynthesis: withRefreshLock(disableTextualSynthesis),
    disableStudiesAppraisal: withRefreshLock(disableStudiesAppraisal),
    submitComplaint: withRefreshLock(submitComplaint),
    appraiseStudy: withRefreshLock(appraiseStudy),
    setOxfordLevelOfEvidenceStudy: withRefreshLock(setOxfordLevelOfEvidenceStudy),
    clearStudyAppraisal: withRefreshLock(clearStudyAppraisal),
    clearStudyOxfordLevelOfEvidence: withRefreshLock(clearStudyOxfordLevelOfEvidence),
    updateStudy: withRefreshLock(updateStudy),
    addAttributeStructure: withRefreshLock(addAttributeStructure),
    deleteAttributeStructure: withRefreshLock(deleteAttributeStructure),
    editAttributeStructure: withRefreshLock(editAttributeStructure),
    lockPlan: withRefreshLock(lockPlan),
    addImportSourceToPlan: withRefreshLock(addImportSourceToPlan),
    importRisSearch: withRefreshLock(importRisSearch),
    addFullTextCriterion: withRefreshLock(addFullTextCriterion),
    addTitleAndAbstractCriterion: withRefreshLock(addTitleAndAbstractCriterion),
    deletFullTextCriterion: withRefreshLock(deletFullTextCriterion),
    deleteTitleAndAbstractCriterion: withRefreshLock(deleteTitleAndAbstractCriterion),
    enableTitleAndAbstractScreening: withRefreshLock(enableTitleAndAbstractScreening),
    disableTitleAndAbstractScreening: withRefreshLock(disableTitleAndAbstractScreening),
    unlockPlan: withRefreshLock(unlockPlan),
    importCitationSearch: withRefreshLock(importCitationSearch),
    answerStudyQuestion: withRefreshLock(answerStudyQuestion),
    addPicoInclusionCriterion: withRefreshLock(addPicoInclusionCriterion),
    deletePicoInclusionCriterion: withRefreshLock(deletePicoInclusionCriterion),
    addAppraisalCriterionAnswer: withRefreshLock(addAppraisalCriterionAnswer),
    deleteAppraisalCriterionAnswer: withRefreshLock(deleteAppraisalCriterionAnswer),
    setStudyPeerReviewStatus: withRefreshLock(setStudyPeerReviewStatus),
    clearStudyPeerReviewStatus: withRefreshLock(clearStudyPeerReviewStatus),
    updateCslStyle: withRefreshLock(updateCslStyle),
    enablePicoInclusionCriteria: withRefreshLock(enablePicoInclusionCriteria),
    disablePicoInclusionCriteria: withRefreshLock(disablePicoInclusionCriteria),
    addInclusionCriterion: withRefreshLock(addInclusionCriterion),
    deleteInclusionCriterion: withRefreshLock(deleteInclusionCriterion),
    importSearch: withRefreshLock(importSearch),
    isLocked,
    isPlanReadonly,
    isReviewReadonly,
    entity: review,
    filteredReviewItems,
    includedReviewItems,
    filteredIncludedReviewItems,
    filtering,
    sorting,
    areInvalidStudiesDisplayed,
    searchesBySource,
    sortedAttributes,
    parentStudyIds,
    maybeParentStudyIds,
    inclusionCriteria,
    lockReview: withRefreshLock(lockReview),
    unlockReview: withRefreshLock(unlockReview),
    updateSynthesisPlanSummary: withRefreshLock(updateSynthesisPlanSummary),
    updateTextualSynthesisDescription: withRefreshLock(updateTextualSynthesisDescription),
    updateSearchStrategySummary: withRefreshLock(updateSearchStrategySummary),
    dataExtractionProvider,
    getDataExtraction,
    createEvaluator: withRefreshLock(createEvaluator),
    updateEvaluator: withRefreshLock(updateEvaluator),
    deleteEvaluator: withRefreshLock(deleteEvaluator),
    enableEvidenceLevelInclusionCriteria: withRefreshLock(enableEvidenceLevelInclusionCriteria),
    disableEvidenceLevelInclusionCriteria: withRefreshLock(disableEvidenceLevelInclusionCriteria),
    enableTimeLimtsInclusionCriteria: withRefreshLock(enableTimeLimtsInclusionCriteria),
    disableTimeLimtsInclusionCriteria: withRefreshLock(disableTimeLimtsInclusionCriteria),
    updateInclusionCriteriaEvidenceLevel: withRefreshLock(updateInclusionCriteriaEvidenceLevel),
    updateInclusionCriteriaTimeLimit: withRefreshLock(updateInclusionCriteriaTimeLimit),
    reviewItemJobStatuses,
    finalizeReviewItemSynthesisAttribute: withRefreshLock(finalizeReviewItemSynthesisAttribute),
    unfinalizeReviewItemSynthesisAttribute: withRefreshLock(unfinalizeReviewItemSynthesisAttribute),
    clearAttributeAiSuggestion: withRefreshLock(clearAttributeAiSuggestion),
    editAttributeAiSuggestion: withRefreshLock(editAttributeAiSuggestion),
    saveMedicalAlternativesAttributeValue: withRefreshLock(saveMedicalAlternativesAttributeValue),

    pauseJobs: withRefreshLock(pauseJobs),
    resumeJobs: withRefreshLock(resumeJobs),
    pauseAutoScreening: withRefreshLock(pauseAutoScreening),
    resumeAutoScreening: withRefreshLock(resumeAutoScreening),
    pauseAutoExtraction: withRefreshLock(pauseAutoExtraction),
    resumeAutoExtraction: withRefreshLock(resumeAutoExtraction),
    pauseAutoSynthesis: withRefreshLock(pauseAutoSynthesis),
    resumeAutoSynthesis: withRefreshLock(resumeAutoSynthesis),
    pauseAutoSynthesisDelta: withRefreshLock(pauseAutoSynthesisDelta),
    resumeAutoSynthesisDelta: withRefreshLock(resumeAutoSynthesisDelta),
    addAttributeHighlight: withRefreshLock(addAttributeHighlight),
    getAttributeHighlights,
    deleteHighlight: withRefreshLock(deleteHighlight),
    addScreeningHighlight: withRefreshLock(addScreeningHighlight),
    getScreeningHighlights,
    deleteScreeningHighlight: withRefreshLock(deleteScreeningHighlight),
    acceptAllAutoScreeningSuggestions: withRefreshLock(acceptAllAutoScreeningSuggestions),
    findPreviousReviewVersion,
    getReviewSynthesisValue,
    getPreviousReviewSynthesisValue,
    getArticlePdfPrice,
    createArticlePdfOrder,
    medicalAlternativeNames,
  };
}
